import { EndwiseMultiSelect } from "@/client/components/endwise-multi-select";
import { Spacer } from "@/client/components/spacer";
import { TinyText } from "@/client/components/tiny-text";
import { useUserCards } from "@/client/hooks";
import { DEFAULT_FORM_VALUES, useStore } from "@/client/store";
import { MOBILE_BREAKPOINT } from "@/constants";
import {
  CURRENCIES,
  Currency,
  ISSUERS,
  NETWORKS,
  type CreditCard,
} from "@/data/schemas";
import { ABBREVIATIONS } from "@/data/valuations";
import { SortMethods, type Filters } from "@/server/api/routers/schemas";
import { VALUATION_METHODS } from "@/utils/calculations";
import type { CardReason } from "@/utils/eligibility";
import { enumToText } from "@/utils/enums";
import { LAST_UPDATED } from "@card-data/exports/metadata";
import {
  Alert,
  Anchor,
  Box,
  Burger,
  Button,
  Container,
  Group,
  Input,
  List,
  Loader,
  NumberInput,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconInfoCircle, IconRestore, IconSearch } from "@tabler/icons-react";
import { format } from "date-fns";
import { useMemo } from "react";

const reducer = (reasons: CardReason[]): Record<string, CreditCard[]> => {
  const buckets: Record<string, CreditCard[]> = {};
  for (const reason of reasons) {
    const key = reason.reasons[0] as string;
    if (key === undefined) continue;
    if (!buckets[key]) buckets[key] = [];
    (buckets[key] as CreditCard[]).push(reason.card);
  }

  return buckets;
};

export const SearchFormView = ({
  form,
  ineligibleCards,
}: {
  form: UseFormReturnType<Filters>;
  ineligibleCards: CardReason[] | undefined;
}) => {
  const { filters } = useStore();
  const userCards = useUserCards();

  const theme = useMantineTheme();
  const buckets = useMemo(
    () => reducer(ineligibleCards ?? []),
    [ineligibleCards],
  );

  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints[MOBILE_BREAKPOINT]})`,
  );

  const lastUpdatedDate = useMemo(() => new Date(LAST_UPDATED), []);

  /**
   * Determination needs to be a little complex.
   * Really easy to mistakenly hide it on desktop.
   *
   * The decision here is basically
   * - Keep a "mobile only" toggle
   * - The determination is then that it's open IF DESKTOP or IF MOBILE TOGGLE IS ENABLED
   */
  const [isOpenedAtMobileResolutions, { toggle }] = useDisclosure();
  const opened = useMemo(() => {
    if (!isMobile) {
      // always display on desktop
      return true;
    }
    return isOpenedAtMobileResolutions;
  }, [isMobile, isOpenedAtMobileResolutions]);

  return (
    <Container>
      <Box mt={"md"}>
        <Box hiddenFrom={MOBILE_BREAKPOINT}>
          <Group gap={"xs"}>
            <Burger
              size={"sm"}
              opened={isOpenedAtMobileResolutions}
              onClick={toggle}
            />
            <Text fz={"lg"}>Info & Filters</Text>
            <Button
              size="xs"
              variant="outline"
              color="red"
              onClick={() => form.setValues(DEFAULT_FORM_VALUES)}
            >
              Reset To Defaults
            </Button>
          </Group>
        </Box>

        <Group visibleFrom={MOBILE_BREAKPOINT} gap={"xs"}>
          <Text fz={"lg"}>Info & Filters</Text>
          <Button
            size="xs"
            variant="outline"
            color="red"
            onClick={() => form.setValues(DEFAULT_FORM_VALUES)}
            leftSection={<IconRestore size="18px" />}
          >
            Reset
          </Button>
        </Group>

        <Box style={{ display: opened ? "block" : "none" }} pt={"sm"}>
          <Stack>
            <Text size={"sm"}>
              Offer Optimist is a powerful credit card sign up bonus comparison
              tool. Data is updated every few days and programmatically
              available via the{" "}
              <Anchor
                href={"https://github.com/aacitelli/credit-card-bonuses-api"}
              >
                Companion API
              </Anchor>
              .
            </Text>

            <Text size={"sm"}>
              Cards are valued based on estimated value of their points, which
              is often more than 1 cent-per-point when redeemed for travel.
              Click on the question mark next to a value for the full
              calculation. If you just want pure cashback, select "Pure
              Cashback" under the "Valuation Method" setting!
            </Text>

            <Text size={"sm"}>
              We DO NOT use affiliate links in order to remain unbiased. If you
              like the site, support it for free by using referral links (when
              listed) and sharing with friends!
            </Text>
            <Text fz={"sm"}>
              Last Updated <b>{format(lastUpdatedDate, "MMM dd, yyyy")}</b>.
            </Text>
          </Stack>

          {!!userCards.data && (
            <Box my={"sm"}>
              <Title order={4}>Your Cards</Title>
              <hr />
              <Spacer>
                <Text fz={"sm"} c={"dimmed"}>
                  We filtered out{" "}
                  <b>
                    {Object.entries(buckets).reduce((acc, elem) => {
                      return acc + elem.length;
                    }, 0)}
                  </b>{" "}
                  cards based on eligibility rules and your saved cards.
                </Text>
                {userCards.isLoading && <Loader />}
                {userCards.data.length === 0 && (
                  <Alert color="gray" icon={<IconInfoCircle />}>
                    {
                      "You haven't added any cards yet. By adding your cards, bonuses will automatically be filtered out based on eligibility like 5/24, 1/48, and more!"
                    }
                  </Alert>
                )}
                {Object.entries(buckets).length === 0 && (
                  <Text>
                    No cards were filtered out by eligibility rules. If this is
                    unexpected (i.e. you have recently applied for cards and
                    expect to be ineligible for others), make sure you've added
                    all your cards under "My Cards" in the sidebar!
                  </Text>
                )}
                <Stack gap={"xs"}>
                  {Object.entries(buckets)
                    .map(([reason, cards]) => {
                      return { reason, cards };
                    })
                    .map((reason) => {
                      return (
                        <Box
                          key={reason.reason}
                          style={{
                            paddingLeft: `5px ${theme.colors.blue[5]} solid`,
                          }}
                        >
                          <Text fz={"sm"}>{reason.reason.split(",")[0]}</Text>
                          {reason.cards
                            .sort((a, b) => {
                              if (a.issuer !== b.issuer)
                                return a.issuer.localeCompare(b.issuer);
                              return a.name.localeCompare(b.name);
                            })
                            .map((card, index) => (
                              <Text
                                key={enumToText(card.issuer) + card.name}
                                fz={"xs"}
                                c={"dimmed"}
                              >
                                ({index + 1}){" "}
                                {`${enumToText(card.issuer)} ${card.name}`}
                                <br />
                              </Text>
                            ))}
                        </Box>
                      );
                    })}
                </Stack>
              </Spacer>
            </Box>
          )}

          <Spacer>
            <Title order={4}>Filters</Title>
            <hr />
            <TextInput
              leftSection={<IconSearch />}
              onChange={(event) =>
                form.setValues({
                  ...filters,
                  name: event.currentTarget.value,
                })
              }
              value={filters.name}
              label={"Card Name"}
            />
            <Input.Wrapper>
              <Select
                label="Sort Method"
                value={form.values.sortMethod}
                onChange={(v) => {
                  if (v === null) return;
                  form.setValues({ ...form.values, sortMethod: v });
                }}
                data={Object.values(SortMethods)}
                comboboxProps={{ offset: 0 }}
              />
              <Text fz={"xs"} c={"dimmed"}>
                Sorting by percent return? Set the "minimum value" filter below
                so low-spend, low-value bonuses are filtered.
              </Text>
            </Input.Wrapper>

            <EndwiseMultiSelect
              label={"Issuers"}
              get={() => filters.issuers}
              set={(v) =>
                form.setValues({
                  ...filters,
                  issuers: v as any,
                })
              }
              defaultValue={Object.values(ISSUERS)}
            />
            <EndwiseMultiSelect
              label={"Networks"}
              get={() => filters.networks}
              set={(v) =>
                form.setValues({
                  ...filters,
                  networks: v as any,
                })
              }
              defaultValue={Object.values(NETWORKS)}
            />

            <Input.Wrapper label={"Travel or Cashback?"}>
              <TinyText>
                Derived from whether the card{"'"}s main currency is convertable
                to cash.
              </TinyText>
              <EndwiseMultiSelect
                get={() => filters.travelOrCashback}
                set={(v) =>
                  form.setValues({
                    ...filters,
                    travelOrCashback: v as any,
                  })
                }
                defaultValue={["Travel", "Cashback"]}
              />
            </Input.Wrapper>

            <Input.Wrapper label={"Business or Personal?"}>
              <EndwiseMultiSelect
                get={() => filters.businessOrPersonal}
                set={(v) =>
                  form.setValues({
                    ...filters,
                    businessOrPersonal: v as any,
                  })
                }
                defaultValue={["Personal", "Business"]}
              />
            </Input.Wrapper>

            <Input.Wrapper label={"Counts Towards 5/24"}>
              <Text fz={"xs"} c={"dimmed"}>
                No clue what this is?{" "}
                <Anchor
                  size="xs"
                  href={
                    "https://thepointsguy.com/guide/ultimate-guide-chase-5-24-rule/"
                  }
                >
                  Read up here.
                </Anchor>
              </Text>
              <EndwiseMultiSelect
                get={() => filters.showCardsThatCountTowards524}
                set={(v) =>
                  form.setValues({
                    ...filters,
                    showCardsThatCountTowards524: v as any,
                  })
                }
                defaultValue={["Yes", "No"]}
              />
            </Input.Wrapper>

            <Input.Wrapper label={"Min % of Historical Max"}>
              <Text fz={"xs"} c={"dimmed"}>
                Historical = Highest bonus w/in last two years.
              </Text>
              <Radio.Group
                value={filters.minimumPercentOfHistoricalMax}
                onChange={(v) =>
                  form.setValues({
                    ...filters,
                    minimumPercentOfHistoricalMax: v as any,
                  })
                }
              >
                <Stack>
                  {[
                    "Best Ever",
                    "Great (95%)",
                    "Good (80%)",
                    "Decent (60%)",
                    "All",
                  ].map((value) => (
                    <Radio value={value} label={value} key={value} />
                  ))}
                </Stack>
              </Radio.Group>
            </Input.Wrapper>

            <NumberInput
              onChange={(v) => {
                if (v === "") {
                  form.setValues({
                    ...filters,
                    minimumValue: undefined,
                  });
                  return;
                }
                form.setValues({
                  ...filters,
                  minimumValue: typeof v === "string" ? Number.parseInt(v) : v,
                });
              }}
              value={filters.minimumValue}
              label={"Minimum Value"}
              placeholder={"1000"}
            />

            <Title order={4}>Annual Fee & Spend</Title>
            <hr />
            <Spacer>
              <Input.Wrapper label={"Annual Fee"}>
                <SimpleGrid cols={2}>
                  <NumberInput
                    onChange={(v) => {
                      if (v === "") {
                        form.setValues({
                          ...filters,
                          minimumAnnualFee: undefined,
                        });
                        return;
                      }
                      form.setValues({
                        ...filters,
                        minimumAnnualFee:
                          typeof v === "string" ? Number.parseFloat(v) : v,
                      });
                    }}
                    value={filters.minimumAnnualFee}
                    placeholder={"Min"}
                  />
                  <NumberInput
                    onChange={(v) => {
                      if (v === "") {
                        form.setValues({
                          ...filters,
                          maximumAnnualFee: undefined,
                        });
                        return;
                      }
                      form.setValues({
                        ...filters,
                        maximumAnnualFee:
                          typeof v === "string" ? Number.parseFloat(v) : v,
                      });
                    }}
                    value={filters.maximumAnnualFee}
                    placeholder={"Max"}
                  />
                </SimpleGrid>
              </Input.Wrapper>

              <Input.Wrapper label={"Total Spend"}>
                <SimpleGrid cols={2}>
                  <NumberInput
                    onChange={(v) => {
                      if (v === "") {
                        form.setValues({
                          ...filters,
                          minimumSpend: undefined,
                        });
                        return;
                      }
                      form.setValues({
                        ...filters,
                        minimumSpend:
                          typeof v === "string" ? Number.parseFloat(v) : v,
                      });
                    }}
                    value={filters.minimumSpend}
                    placeholder={"Min"}
                  />
                  <NumberInput
                    onChange={(v) => {
                      if (v === "") {
                        form.setValues({
                          ...filters,
                          maximumSpend: undefined,
                        });
                        return;
                      }
                      form.setValues({
                        ...filters,
                        maximumSpend:
                          typeof v === "string" ? Number.parseFloat(v) : v,
                      });
                    }}
                    value={filters.maximumSpend}
                    placeholder={"Max"}
                  />
                </SimpleGrid>
              </Input.Wrapper>

              <Input.Wrapper>
                <NumberInput
                  onChange={(v) => {
                    if (v === "") {
                      form.setValues({
                        ...filters,
                        maximumMonthlySpend: undefined,
                      });
                      return;
                    }
                    form.setValues({
                      ...filters,
                      maximumMonthlySpend:
                        typeof v === "string" ? Number.parseFloat(v) : v,
                    });
                  }}
                  value={filters.maximumMonthlySpend}
                  label={"Maximum Monthly Spend"}
                />
                <Text size={"xs"} c={"dimmed"}>
                  Filters out offers where you can't hit the minimum spend. If
                  you can't hit the spend on ANY offers, the whole card is
                  hidden.
                </Text>
              </Input.Wrapper>
            </Spacer>

            <Title order={4}>Valuation Settings</Title>
            <hr />
            <Spacer>
              <Select
                data={Object.values(VALUATION_METHODS)}
                onChange={(v) =>
                  form.setValues({
                    ...filters,
                    valuationMethod: v ?? VALUATION_METHODS.DEFAULT,
                  })
                }
                value={filters.valuationMethod}
                label={"Valuation Method"}
                comboboxProps={{ offset: 0 }}
              />

              {filters.valuationMethod === VALUATION_METHODS.CUSTOM && (
                <List listStyleType={"none"}>
                  <SimpleGrid cols={3} spacing={"xs"}>
                    {Object.values(CURRENCIES)
                      .filter((currency) => currency !== "USD")
                      .sort()
                      .map((currency: Currency) => {
                        const value = filters.customValuations[currency] ?? 0;
                        return (
                          <List.Item key={currency}>
                            <NumberInput
                              label={ABBREVIATIONS[currency] ?? currency}
                              size={"xs"}
                              hideControls={true}
                              decimalScale={2}
                              onChange={(v) => {
                                const numValue =
                                  v === ""
                                    ? 0
                                    : typeof v === "string"
                                      ? Number.parseFloat(v)
                                      : (v ?? 0);
                                form.setValues({
                                  ...filters,
                                  customValuations: {
                                    ...filters.customValuations,
                                    [currency]: numValue,
                                  },
                                });
                              }}
                              value={value}
                            />
                          </List.Item>
                        );
                      })}
                  </SimpleGrid>
                </List>
              )}

              <Input.Wrapper label={"Military"}>
                <Text fz="xs" c={"dimmed"}>
                  Removes annual fee for some issuers.
                </Text>
                <Radio.Group
                  onChange={(v) => {
                    form.setValues({
                      ...filters,
                      isMilitary: v as "Yes" | "No",
                    });
                  }}
                  value={filters.isMilitary}
                >
                  <Stack>
                    {["No", "Yes"].map((value) => (
                      <Radio value={value} label={value} key={value} />
                    ))}
                  </Stack>
                </Radio.Group>
              </Input.Wrapper>

              <Input.Wrapper label={"Current Universal Cashback Currency"}>
                <Select
                  data={Object.values(CURRENCIES)
                    .sort()
                    .map((v) => {
                      return {
                        value: v,
                        label: enumToText(v),
                      };
                    })}
                  onChange={(v) => {
                    form.setValues({
                      ...filters,
                      currentUniversalCashbackCurrency:
                        (v as Currency) ?? "USD",
                    });
                  }}
                  value={filters.currentUniversalCashbackCurrency}
                  comboboxProps={{ offset: 0 }}
                />
                <Text fz="xs" c={"dimmed"}>
                  Adjusts valuations based on opportunity cost.
                </Text>
              </Input.Wrapper>

              <NumberInput
                value={filters.currentUniversalCashback}
                decimalScale={2}
                onChange={(n) => {
                  if (n === "") {
                    form.setValues({ ...filters, currentUniversalCashback: 0 });
                    return;
                  }
                  form.setValues({
                    ...filters,
                    currentUniversalCashback:
                      typeof n === "string" ? Number.parseFloat(n) : n,
                  });
                }}
                label={"Current Universal Cashback %"}
              />
            </Spacer>
          </Spacer>
        </Box>
      </Box>
    </Container>
  );
};
